/**
 * CAD waterfall item collections for /v2/pricing/submitWaterfall.
 * Keep JSON bucket names and profitability_payload_category aligned with the
 * pricing engine so a later shared-module cleanup can merge without contract changes.
 */

export const CAD_ITEM_COLLECTIONS = [
  "saleItems",
  "addOns",
  "additionalItems",
  "mpwItems",
  "sisServices",
] as const;

/** Wire-format field name required by the pricing engine. Do not rename the string. */
export const CAD_PAYLOAD_CATEGORY_FIELD = "profitability_payload_category";

export type CadItemCollection = (typeof CAD_ITEM_COLLECTIONS)[number];

const isCadItemCollection = (key: string): key is CadItemCollection =>
  (CAD_ITEM_COLLECTIONS as readonly string[]).includes(key);

/** Resolves the payload bucket key for a line item (DB field or collection fallback). */
export const resolveCadPayloadBucket = (
  item: Record<string, any> = {},
  fallbackBucket: string = "saleItems"
): CadItemCollection => {
  const category = item[CAD_PAYLOAD_CATEGORY_FIELD];
  if (typeof category === "string" && isCadItemCollection(category)) {
    return category;
  }
  if (isCadItemCollection(fallbackBucket)) {
    return fallbackBucket;
  }
  return "saleItems";
};

export const tagCadItemsWithPayloadCategory = (
  items: any[] = [],
  bucket: CadItemCollection
) =>
  items.map((item) => ({
    ...item,
    [CAD_PAYLOAD_CATEGORY_FIELD]: resolveCadPayloadBucket(item, bucket),
  }));

/**
 * Re-groups line items into saleItems / addOns / mpwItems / etc. using
 * profitability_payload_category from the DB (or cart segregation).
 */
export const organizeCadPayloadByCategory = (data: Record<string, any> = {}) => {
  const buckets: Record<CadItemCollection, any[]> = {
    saleItems: [],
    addOns: [],
    additionalItems: [],
    mpwItems: [],
    sisServices: [],
  };

  CAD_ITEM_COLLECTIONS.forEach((key) => {
    (data[key] || []).forEach((item: any) => {
      const bucket = resolveCadPayloadBucket(item, key);
      buckets[bucket].push({
        ...item,
        [CAD_PAYLOAD_CATEGORY_FIELD]: bucket,
      });
    });
  });

  return { ...data, ...buckets };
};

export const pickCadItemCollections = (data: Record<string, any> = {}) => ({
  saleItems: tagCadItemsWithPayloadCategory(data.saleItems, "saleItems"),
  addOns: tagCadItemsWithPayloadCategory(data.addOns, "addOns"),
  additionalItems: tagCadItemsWithPayloadCategory(
    data.additionalItems,
    "additionalItems"
  ),
  mpwItems: tagCadItemsWithPayloadCategory(data.mpwItems, "mpwItems"),
  sisServices: tagCadItemsWithPayloadCategory(data.sisServices, "sisServices"),
});
